/**
 * Studio's copy for the audit trail (RFC 0006 §6, wave 6), kept beside
 * strings.ts and spread into it: `strings.audit` and `strings.problems`.
 * Plain data — it imports nothing.
 */
export const auditStrings = {
  back: "Projects",
  retry: "Read again",
  nav: "Audit log",
  needs: (permission: string, who: string) => `This needs ${permission} (${who}).`,
  log: {
    title: "Audit log",
    lead: "Everything that changed in this workspace, in the order it happened: who did it, what they did and what it was about. Each entry is chained to the one before it, so an export can be checked offline. Entries never hold text — only identifiers and the shape of what changed.",
    noAccess: "The audit log is for owners and admins (audit.read). You can't read it.",
    loading: "Loading entries…",
    failed: "The audit log could not be read, so nothing below is known — not even whether there are entries.",
    empty: "No entries match.",
    emptyFiltered: "No entries match these filters.",
    exportsLink: "Exports",
    filters: "Filters",
    from: "From",
    to: "Before",
    actor: "Actor",
    actorHint: "person:<id>, token:<id>, system:<name> or unknown",
    action: "Action",
    actionHint: "An event type, e.g. release.published",
    source: "Source",
    anySource: "Any",
    sources: { outbox: "Domain event", direct: "Direct (sign-in, tool call)", import: "Imported (v0.3)" } as Record<string, string>,
    project: "Project ID",
    apply: "Apply",
    clear: "Clear",
    columns: { sequence: "Seq", when: "When", actor: "Actor", action: "Action", about: "About", source: "Source" },
    more: "Load more",
    loadingMore: "Loading…",
    moreFailed: "The next page could not be read.",
    shown: (n: number) => `${n.toLocaleString()} ${n === 1 ? "entry" : "entries"} shown, newest first.`,
    invalidRange: "“Before” must be later than “From”.",
  },
  entry: {
    title: (sequence: string) => `Audit entry ${sequence}`,
    back: "Audit log",
    loading: "Loading the entry…",
    failed: "The entry could not be read.",
    notFound: "There is no such entry — or it belongs to a project you can't see.",
    lead: "Content-free by construction: identifiers and selectors are shown as they are, everything else only as its shape (string(len=12)).",
    fields: {
      when: "When",
      actor: "Actor",
      action: "Action",
      source: "Source",
      about: "About",
      project: "Project",
      locale: "Locale",
      event: "Event ID",
      request: "Request ID",
      trace: "Trace ID",
      prev: "Previous hash",
      hash: "Hash",
      summary: "Summary",
    },
    chain: "The hash is sha256(previous hash ‖ the entry's canonical JSON). Export a range and verify it offline to prove none of it was changed.",
    previous: "Previous entry",
    next: "Next entry",
    noSummary: "No details beyond the above.",
  },
  exports: {
    title: "Audit exports",
    lead: "An export is a signed, tamper-evident copy of a range of the audit log: two files, entries.jsonl and manifest.json. It is always the whole workspace's trail for the range — never one project's — and can be checked offline by anyone holding the public key.",
    back: "Audit log",
    noAccess: "Exporting the audit log needs audit.export, which only owners hold by default.",
    unavailable: "This deployment can't export the audit log: it has no audit signing key, or has exports switched off. Ask whoever runs it to configure GLOSSA_AUDIT_SIGNING_KEY and GLOSSA_AUDIT_EXPORTS_ENABLED.",
    loading: "Loading exports…",
    failed: "The exports could not be read.",
    empty: "No exports yet.",
    start: "Start an export",
    rangeKind: "Range",
    byTime: "By time (at most 31 days)",
    bySequence: "By sequence (up to 1,000,000 entries)",
    from: "From",
    to: "Before",
    firstSequence: "First sequence",
    lastSequence: "Last sequence",
    lastHint: "Leave empty for the log's current end.",
    submit: "Export",
    invalidTime: "Give both a start and a later end.",
    invalidSequence: "Give the first sequence; the last, if given, must not be smaller.",
    started: "The export was queued. It updates here until it finishes.",
    columns: { job: "Export", state: "State", range: "Range", entries: "Entries", key: "Signed with", files: "Files", created: "Started" },
    states: { queued: "Queued", running: "Running", succeeded: "Ready", failed: "Failed" } as Record<string, string>,
    sequences: (a: number, b: number) => `Sequences ${a.toLocaleString()}–${b.toLocaleString()}`,
    timeRange: (a: string, b: string) => `${a} to ${b}`,
    download: { entries: "entries.jsonl", manifest: "manifest.json" },
    downloadLabel: (name: string, bytes: string) => `Download ${name} (${bytes})`,
    expires: (when: string) => `Files are kept until ${when}.`,
    expired: "Files deleted after the retention period.",
    failedWith: (code: string) => `Failed (${code})`,
    rangeNotContiguous: "That time range isn't one unbroken stretch of the log — imported v0.3 history sits inside it. Export by sequence instead.",
    verify: {
      title: "Verify an export offline",
      lead: "Verification needs no account and no network. Pin the public key once, then check any export you downloaded; it names the first thing that does not hold.",
      keyId: "Published key",
      keyActive: "active",
      keyRetired: "retired",
      noKeys: "This deployment publishes no audit keys.",
      keysFailed: "The published keys could not be read.",
      saveKeys: "Save the key document once and keep it:",
      command: "Then verify:",
      keyMatches: (id: string) => `This export was signed with ${id}, which the deployment publishes.`,
      keyMissing: (id: string) => `This export was signed with ${id}, which the deployment does not publish. Verification would fail with unknown_key.`,
      why: "Do not take the key from the export itself: a signature checked with a key that travels with the file proves nothing.",
    },
  },
};

/** Sentences for the audit API's problem codes, merged into `strings.problems`. */
export const auditProblems: Record<string, string> = {
  audit_export_unavailable: "This deployment can't export the audit log: it has no audit signing key, or has exports switched off.",
  range_not_contiguous: "That time range isn't one unbroken stretch of the log — imported v0.3 history sits inside it. Export by sequence instead.",
  range_too_long: "That range is too long: at most 31 days, or 1,000,000 entries. Split it into several exports.",
  sequence_out_of_range: "That sequence is past the end of the log.",
  invalid_range: "Give a time range (from and before) or a sequence range (first, and optionally last).",
  invalid_query: "Those filters aren't valid.",
  export_not_ready: "The export hasn't finished yet.",
  file_expired: "The export's files were deleted after the retention period. Start a new export.",
};
